import React, { useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faXmark } from "@fortawesome/free-solid-svg-icons";

/**
 * DS Modal — 일반 popup container.
 * figma 307:2798 등 popup pattern.
 *
 * - backdrop: black 0.5, fixed full screen, click → close
 * - frame: white bg, radius 12, overflow hidden, centered
 * - title bar (h=70): title text + close X (32×32)
 * - body slot
 *
 * @param {object} props
 * @param {boolean} props.open
 * @param {() => void} props.onClose
 * @param {string} props.title
 * @param {number} [props.width=960]
 * @param {number} [props.height=720]
 * @param {boolean} [props.overflowVisible=false] - 내부 dropdown 팝오버가 모달 밖으로 나가야 할 때 true
 * @param {boolean} [props.hideTitleDivider=false] - 타이틀 하단 구분선 숨김 (헤더가 본문으로 이어지는 디자인)
 * @param {string} [props.className] - .ds-modal 에 덧붙일 클래스. 화면별 셸 보정용 스코프 훅
 * @param {boolean} [props.closeOnEsc=true] - false 면 ESC 키로 닫히지 않음 (X 버튼은 유지)
 * @param {React.ReactNode} props.children - body
 */
const DSModal = ({
  open,
  onClose,
  title,
  desc,
  width = 960,
  height = 720,
  overflowVisible = false,
  hideTitleDivider = false,
  className = "",
  closeOnEsc = true,
  children,
}) => {
  // ESC 키 닫기
  useEffect(() => {
    if (!open || !closeOnEsc) return undefined;
    const handler = (e) => {
      if (e.key === "Escape") onClose && onClose();
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [open, closeOnEsc, onClose]);

  if (!open) return null;
  return (
    // backdrop 클릭으로는 닫지 않음 (실수 닫힘 방지). 닫기는 X 버튼/ESC(closeOnEsc) 로만. showAlert(AlertModal) 는 별도.
    <div className="ds-modal-backdrop">
      <div
        className={`ds-modal${className ? ` ${className}` : ""}`}
        style={{
          width,
          height,
          ...(overflowVisible ? { overflow: "visible" } : {}),
        }}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <div
          className={`ds-modal__title${
            hideTitleDivider ? " ds-modal__title--no-divider" : ""
          }`}
        >
          <span className="ds-modal__title-text">
            {title}
            {desc ? <span className="ds-modal__title-desc">{desc}</span> : null}
          </span>

          <button
            type="button"
            className="ds-modal__close"
            onClick={onClose}
            aria-label="닫기"
          >
            <FontAwesomeIcon icon={faXmark} />
          </button>
        </div>
        <div
          className="ds-modal__body"
          style={overflowVisible ? { overflow: "visible" } : undefined}
        >
          {children}
        </div>
      </div>
    </div>
  );
};

export default React.memo(DSModal);
